import { useMemo } from "react"
import useSWR from "swr"
import { $PropertyType } from "utility-types"
import { IUser } from "../Auth"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { TCurrencyCode, TFormattedMoney } from "@sembark-travel/money"

interface IBreakdown {
	reference?: { id: number; name: string }
	day_offset: number
	instalment_breakup_in_percent: number
}
interface NewItemCredentials {
	breakdowns?: IBreakdown[]
}

export type TCustomerPaymentPreference = {
	id: number
	name: string
	created_at: string
	// Only with `include: "created_by"`
	created_by?: IUser
	deleted_at: string | null
	// Each condition is null when the rule does not use it, and both are null on
	// the fallback rule
	days_before_trip_start: string | null
	days_before_trip_start_condition: TPreferenceConditionOperator | null
	trip_duration: string | null
	trip_duration_condition: TPreferenceConditionOperator | null
	// Null on a rule embedded in a payment policy
	trips_count: number | null
	breakdowns: TInstalmentBreakdown[]
	// Null on the fallback rule
	preference_condition: string | null
	flight_include: boolean | null
	min_first_instalment_amounts: Array<{
		id: number
		currency: TCurrencyCode
		amount: TFormattedMoney
	}>
}

export type TPreferenceConditionOperator = "is_less" | "is_greater"

export type TInstalmentBreakdown = {
	id: number
	preference_id: number
	reference: number
	reference_label: string
	day_offset: number
	instalment_breakup_in_percent: number
}
export type TPaymentReference = { id: number; name: string }

export type TCustomerPaymentPreferencePayload = {
	is_fallback_rule: 1 | 0
	days_before_trip_start?: { condition: string; days: number }
	trip_duration?: { condition: string; nights: number }
	min_first_instalment_amount?: { amount: number; currency: TCurrencyCode }[]
	breakdowns: {
		reference: number
		day_offset: number
		instalment_breakup_in_percent: number
	}[]
	is_include_flight_cost_in_first_instalment: 1 | 0
}

export function customerPaymentPreferencesXHR(xhr: XHRInstance) {
	return {
		async get(params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TCustomerPaymentPreference>
				>("/customer-payment-preferences", { params: { include: "created_by", ...params } })
				.then((resp) => resp.data)
		},

		async create(
			payload: TCustomerPaymentPreferencePayload
		): Promise<TCustomerPaymentPreference> {
			return await xhr
				.post<{
					data: TCustomerPaymentPreference
				}>("/customer-payment-preferences", payload)
				.then((resp) => resp.data.data)
		},

		async getReferences(params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TPaymentReference>
				>("/customer-payment-preferences/references", { params })
				.then((resp) => resp.data)
		},
	}
}

export function useCustomerPaymentReferences() {
	const xhr = useXHR()
	const { data, error, isLoading } = useSWR(
		"/customer-payment-preferences/references",
		() => customerPaymentPreferencesXHR(xhr).getReferences()
	)
	const references = useMemo(() => data?.data || [], [data])
	const referenceNames = useMemo(() => {
		const names: Record<number, string> = {}
		for (const ref of references) {
			names[Number(ref.id)] = ref.name
		}
		return names
	}, [references])

	return {
		references,
		referenceNames,
		isLoading,
		error,
	}
}

export function getTotalAmountShare(
	breakdowns: $PropertyType<NewItemCredentials, "breakdowns">
) {
	return (breakdowns || []).reduce(
		(total, { instalment_breakup_in_percent }) =>
			Number(total) + Number(instalment_breakup_in_percent),
		0
	)
}
